import { useEffect } from "react";

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
  httpEquiv?: string;
}

interface DocumentMetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  ogSiteName?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  lang?: string;
}

export default function DocumentMeta({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogType,
  ogSiteName,
  ogLocale,
  twitterCard,
  twitterTitle,
  twitterDescription,
  lang,
}: DocumentMetaProps) {
  useEffect(() => {
    if (title) document.title = title;
    if (lang) document.documentElement.lang = lang;

    const metaTags: MetaTag[] = [];
    if (description) metaTags.push({ name: "description", content: description });
    if (keywords) metaTags.push({ name: "keywords", content: keywords });
    if (ogTitle) metaTags.push({ property: "og:title", content: ogTitle });
    if (ogDescription) metaTags.push({ property: "og:description", content: ogDescription });
    if (ogUrl) metaTags.push({ property: "og:url", content: ogUrl });
    if (ogType) metaTags.push({ property: "og:type", content: ogType });
    if (ogSiteName) metaTags.push({ property: "og:site_name", content: ogSiteName });
    if (ogLocale) metaTags.push({ property: "og:locale", content: ogLocale });
    if (twitterCard) metaTags.push({ name: "twitter:card", content: twitterCard });
    if (twitterTitle) metaTags.push({ name: "twitter:title", content: twitterTitle });
    if (twitterDescription) metaTags.push({ name: "twitter:description", content: twitterDescription });

    const createdElements: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const attr = tag.name ? "name" : "property";
      const attrValue = tag.name || tag.property || "";
      let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
        createdElements.push(el as HTMLElement);
      }
      el.setAttribute("content", tag.content);
    });

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
        createdElements.push(link);
      }
      link.setAttribute("href", canonical);
    }

    return () => {
      createdElements.forEach((el) => el.remove());
    };
  }, []);

  return null;
}